import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom';
import { Editor } from '@tiptap/react';

import { PromptCommandMenu } from './PromptCommandMenu';
import { PromptChipAttrs } from './SlashExtension.types';
import { PromptFill } from './usePromptFillStep';

type PromptChipPopoverProps = {
  editor: Editor;
  /** The chip's position in the document. */
  pos: number;
  /**
   * Called when the step closes; `refocus` puts the caret back in the editor,
   * `saved` says the values were saved (not discarded).
   */
  onClose: (refocus: boolean, saved?: boolean) => void;
};

const chipAt = (editor: Editor, pos: number) => {
  const node = editor.state.doc.nodeAt(pos);
  return node?.type.name === 'promptChip' ? node : null;
};

/**
 * A chip reopened in the composer (VH3_CONNECT #592): its fill step in the
 * prompt menu's card, anchored at the chip. Values or wording change for this
 * message only; the template never changes. Escape or a click outside
 * discards.
 */
export function PromptChipPopover({
  editor,
  pos,
  onClose,
}: PromptChipPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const node = chipAt(editor, pos);
  const attrs = node?.attrs as PromptChipAttrs | undefined;

  useLayoutEffect(() => {
    const anchor = editor.view.nodeDOM(pos);
    const floating = ref.current;
    if (!(anchor instanceof HTMLElement) || !floating) return;
    // Fixed to the viewport, not the document: on a phone the composer sits
    // at the bottom over a scrolled drawer, and the card must stay on screen
    // above it. `size` caps the card to the room left so its list scrolls.
    const update = () =>
      computePosition(anchor, floating, {
        placement: 'top-start',
        strategy: 'fixed',
        middleware: [
          offset(8),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply({ availableHeight, elements }) {
              elements.floating.style.maxHeight = `${Math.max(
                160,
                availableHeight
              )}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
      });
    return autoUpdate(anchor, floating, update);
  }, [editor, pos]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [onClose]);

  const fill = useMemo<PromptFill | null>(
    () =>
      attrs
        ? {
            key: `chip-${pos}`,
            mode: 'reopen',
            chip: attrs,
            onSubmit: chip => {
              const current = chipAt(editor, pos);
              if (current) {
                editor
                  .chain()
                  .focus()
                  .command(({ tr }) => {
                    tr.setNodeMarkup(pos, undefined, {
                      ...current.attrs,
                      values: chip.values ?? {},
                      body: chip.body,
                      edited: !!chip.edited,
                    });
                    return true;
                  })
                  // The caret goes back after the chip, ready to type.
                  .setTextSelection(pos + current.nodeSize)
                  .run();
              }
              onClose(true, true);
            },
            onBack: () => onClose(true),
          }
        : null,
    // The chip's attrs are read when the step opens; a save closes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, pos, onClose]
  );

  if (!fill) return null;

  return createPortal(
    <div
      ref={ref}
      className='prompt-chip-popover'
      data-cy='prompt-chip-popover'
      style={{ position: 'fixed', left: 0, top: 0, zIndex: 1500 }}
    >
      <PromptCommandMenu
        sections={[]}
        onPick={() => undefined}
        enterHint=''
        query=''
        fill={fill}
      />
    </div>,
    document.body
  );
}
